import { useEffect, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { getErrorCodeForUi } from '@/api/errors';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';

import { useTenantRoles, type TenantUserViewModel } from '../api';

/**
 * Replaces a tenant user's full role set (PTV2-1426, `PUT /users/{guid}/roles`). Options come from
 * the tenant's real role catalogue (`useTenantRoles`) rather than free text — mirrors the
 * provider-config role pickers (part B of this feature). Any role the user already carries that
 * ISN'T in the catalogue (e.g. provisioned via the Phase 1 free-text form) is still listed and
 * checked so replacing the set can never silently drop it.
 */
export function AssignTenantUserRolesDialog({
  tenantUuid,
  user,
  onCancel,
  onConfirm,
  pending,
  submitError,
}: {
  readonly tenantUuid: string;
  /** Non-null = open, editing this user's roles. */
  readonly user: TenantUserViewModel | null;
  readonly onCancel: () => void;
  readonly onConfirm: (roles: readonly string[]) => void;
  readonly pending: boolean;
  readonly submitError: unknown;
}): ReactElement {
  const { t } = useTranslation();
  const open = user !== null;
  const rolesQuery = useTenantRoles(open ? tenantUuid : '');
  const [selected, setSelected] = useState<readonly string[]>([]);

  useEffect(() => {
    if (user !== null) setSelected(user.roles);
  }, [user]);

  const catalogRoles = rolesQuery.data ?? [];
  const catalogNames = new Set(catalogRoles.map((r) => r.name));
  // Roles the user already carries that the catalogue doesn't know about — kept visible so a save
  // never drops them.
  const extraRoles = selected.filter((r) => !catalogNames.has(r));
  const options = [...catalogRoles.map((r) => r.name), ...extraRoles];

  function toggle(role: string): void {
    setSelected((prev) => (prev.includes(role) ? prev.filter((r) => r !== role) : [...prev, role]));
  }

  const submitErrorCode = getErrorCodeForUi(submitError);
  const displayName = user !== null && (user.fullName.length > 0 ? user.fullName : user.name);

  return (
    <Dialog open={open} onOpenChange={(next) => { if (!next) onCancel(); }}>
      <DialogContent disableOutsideClose>
        <DialogHeader>
          <DialogTitle>{t('routes.platform.detail.users.assignRoles.title')}</DialogTitle>
          <DialogDescription>
            {t('routes.platform.detail.users.assignRoles.description', { name: displayName })}
          </DialogDescription>
        </DialogHeader>

        {rolesQuery.isPending && (
          <div className="space-y-2" aria-hidden="true">
            {Array.from({ length: 3 }).map((_, i) => (
              <Skeleton key={i} className="h-6 w-full" />
            ))}
          </div>
        )}

        {rolesQuery.isSuccess && options.length === 0 && (
          <p className="text-sm text-muted-foreground" role="status">
            {t('routes.platform.detail.users.assignRoles.empty')}
          </p>
        )}

        {rolesQuery.isSuccess && options.length > 0 && (
          <fieldset className="max-h-64 space-y-2 overflow-y-auto">
            <legend className="sr-only">{t('routes.platform.detail.users.assignRoles.title')}</legend>
            {options.map((role) => (
              <label key={role} className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-3.5 w-3.5"
                  checked={selected.includes(role)}
                  onChange={() => {
                    toggle(role);
                  }}
                />
                {role}
                {!catalogNames.has(role) && (
                  <span className="text-xs text-muted-foreground">
                    {t('routes.platform.detail.users.assignRoles.notInCatalog')}
                  </span>
                )}
              </label>
            ))}
          </fieldset>
        )}

        {submitError !== undefined && submitError !== null && (
          <p role="alert" className="text-sm text-red-600">
            {t('routes.platform.detail.users.assignRoles.errors.submit', { code: submitErrorCode })}
          </p>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onCancel} disabled={pending}>
            {t('routes.platform.detail.users.assignRoles.cancel')}
          </Button>
          <Button
            type="button"
            loading={pending}
            onClick={() => {
              onConfirm(selected);
            }}
          >
            {t('routes.platform.detail.users.assignRoles.submit')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
